import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { User, FileText, Bell, LogOut, RefreshCw, AlertCircle } from 'lucide-react';
import ApplicationTracker from './components/ApplicationTracker';
import VoucherCard from './components/VoucherCard';
import NotificationCenter from './components/NotificationCenter';
import ProfileSettings from './components/ProfileSettings';

const CustomerPortal = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [applications, setApplications] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [customerData, setCustomerData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadCustomerData();
  }, [user, navigate]);

  const loadCustomerData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch applications by customer email
      const { data: appsData, error: appsError } = await supabase?.from('applications')?.select(`
          *,
          dealerships (
            name,
            email,
            phone
          )
        `)?.eq('customer_email', user?.email)?.order('created_at', { ascending: false });

      if (appsError) throw appsError;

      setApplications(appsData || []);

      // Fetch vouchers for approved applications
      if (appsData && appsData?.length > 0) {
        const approvedAppIds = appsData
          ?.filter(app => app?.application_status === 'approved')
          ?.map(app => app?.id);

        if (approvedAppIds?.length > 0) {
          const { data: vouchersData, error: vouchersError } = await supabase?.from('vouchers')?.select('*')?.in('application_id', approvedAppIds)?.order('created_at', { ascending: false });

          if (vouchersError) throw vouchersError;
          setVouchers(vouchersData || []);
        }
      }

      // Set customer data from first application
      if (appsData && appsData?.length > 0) {
        setCustomerData({
          name: appsData?.[0]?.customer_name,
          email: appsData?.[0]?.customer_email,
          phone: appsData?.[0]?.customer_phone
        });
      }

      // Generate notifications based on application status changes
      generateNotifications(appsData || []);

    } catch (err) {
      console.error('Error loading customer data:', err);
      setError('Fehler beim Laden der Daten. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const generateNotifications = (apps) => {
    const newNotifications = [];
    
    apps?.forEach(app => {
      if (app?.application_status === 'approved' && app?.approval_date) {
        newNotifications?.push({
          id: `approved-${app?.id}`,
          type: 'success',
          title: 'Antrag genehmigt',
          message: `Ihr Antrag für ${app?.vehicle_model} wurde genehmigt!`,
          timestamp: app?.approval_date,
          read: false
        });
      } else if (app?.application_status === 'rejected' && app?.rejection_reason) {
        newNotifications?.push({
          id: `rejected-${app?.id}`,
          type: 'error',
          title: 'Antrag abgelehnt',
          message: `Ihr Antrag wurde abgelehnt: ${app?.rejection_reason}`,
          timestamp: app?.updated_at,
          read: false
        });
      } else if (app?.application_status === 'processing') {
        newNotifications?.push({
          id: `processing-${app?.id}`,
          type: 'info',
          title: 'Antrag wird bearbeitet',
          message: `Ihr Antrag für ${app?.vehicle_model} wird derzeit bearbeitet.`,
          timestamp: app?.updated_at,
          read: false
        });
      }
    });

    // Sort by timestamp descending
    newNotifications?.sort((a, b) => new Date(b?.timestamp) - new Date(a?.timestamp));
    setNotifications(newNotifications);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadCustomerData();
    setRefreshing(false);
  };

  const handleLogout = async () => {
    const { error } = await signOut();
    if (!error) {
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Lade Ihre Daten...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Fehler</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={handleRefresh}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                gridbalance THG Premie
              </h1>
              <span className="hidden sm:inline-block text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                Kundenportal
              </span>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Aktualisieren"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-3 sm:px-4 py-2 text-gray-700 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span className="hidden sm:inline">Abmelden</span>
              </button>
            </div>
          </div>
        </div>
      </header>
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                Willkommen zurück, {customerData?.name}!
              </h2>
              <p className="text-gray-600 mt-1">
                Verwalten Sie Ihre THG-Premium-Anträge und Gutscheincodes
              </p>
            </div>
            <div className="hidden lg:flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-gray-500">Gesamt-Anträge</p>
                <p className="text-2xl font-bold text-blue-600">{applications?.length}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Aktive Gutscheine</p>
                <p className="text-2xl font-bold text-green-600">
                  {vouchers?.filter(v => v?.voucher_status === 'active')?.length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm mb-6 overflow-x-auto">
          <nav className="flex space-x-1 p-2 min-w-max">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: FileText },
              { id: 'vouchers', label: 'Gutscheine', icon: FileText },
              { id: 'notifications', label: 'Benachrichtigungen', icon: Bell },
              { id: 'profile', label: 'Profil', icon: User }
            ]?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-colors whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'bg-blue-600 text-white' :'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="font-medium">{tab?.label}</span>
                {tab?.id === 'notifications' && notifications?.filter(n => !n?.read)?.length > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                    {notifications?.filter(n => !n?.read)?.length}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'dashboard' && (
            <ApplicationTracker applications={applications} />
          )}

          {activeTab === 'vouchers' && (
            <div className="space-y-4">
              {vouchers?.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Keine Gutscheine verfügbar
                  </h3>
                  <p className="text-gray-600">
                    Gutscheine werden nach der Genehmigung Ihrer Anträge angezeigt
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {vouchers?.map(voucher => (
                    <VoucherCard 
                      key={voucher?.id} 
                      voucher={voucher}
                      application={applications?.find(app => app?.id === voucher?.application_id)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'notifications' && (
            <NotificationCenter 
              notifications={notifications}
              setNotifications={setNotifications}
            />
          )}

          {activeTab === 'profile' && (
            <ProfileSettings 
              customerData={customerData}
              applications={applications}
              onUpdate={loadCustomerData}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default CustomerPortal;